/**
 * components/VisualMapper.tsx - Visual Field Mapper Component
 * 
 * CHANGE HISTORY:
 * - 251103_1042 [pending] danielbeer: Initial Visual Mapper component
 * 
 * Main component for visual drag-and-drop field mapping.
 * Provides an intuitive interface for mapping source fields to target fields.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useMapper } from '../hooks/useMapper';
import { EditableFieldList } from './EditableFieldList';
import { MappingCanvas } from './MappingCanvas';
import { MapperToolbar } from './MapperToolbar';
import { SuggestionsPanel } from './SuggestionsPanel';
import type { VisualMapperProps, Field } from '../types/index';
import './VisualMapper.css';
import './EditableFieldList.css';

// Commandr will be optional - try to import from dependr
let useShortcut: any = null;
try {
  // @ts-ignore - optional dependency
  const commandr = require('@opcentral/commandr');
  useShortcut = commandr.useShortcut;
} catch (e) {
  // Commandr not available - shortcuts disabled
}

/**
 * VisualMapper - Main visual field mapping component
 * 
 * Provides a drag-and-drop interface for mapping fields between
 * source and target schemas.
 * 
 * @example
 * ```tsx
 * <VisualMapper
 *   sourceFields={sourceFields}
 *   targetFields={targetFields}
 *   onMappingChange={(mappings) => console.log(mappings)}
 *   autoMatch={true}
 *   showConfidence={true}
 * />
 * ```
 */
export const VisualMapper: React.FC<VisualMapperProps> = ({
  sourceFields,
  targetFields,
  initialMappings,
  onMappingChange,
  onValidate,
  autoMatch = false,
  allowMultipleTargets = false,
  allowCombine = true,
  allowCustomTransforms = false,
  theme = 'light',
  showConfidence = true,
  showPreview = false,
  layout = 'horizontal',
  showToolbar = true,
  className = '',
  mapprManager,
  profylrContext,
  aggregatr,
  backtrackr  // Accept backtrackr from props
}) => {
  // Local state for editable fields
  const [localSourceFields, setLocalSourceFields] = useState<Field[]>(sourceFields);
  const [localTargetFields, setLocalTargetFields] = useState<Field[]>(targetFields);

  // Update local state when props change
  useEffect(() => {
    setLocalSourceFields(sourceFields);
  }, [sourceFields]);

  useEffect(() => {
    setLocalTargetFields(targetFields);
  }, [targetFields]);

  // Use mapper hook for state management
  const mapper = useMapper({
    sourceFields: localSourceFields,
    targetFields: localTargetFields,
    initialMappings,
    onMappingChange,
    onValidate,
    autoMatch,
    allowMultipleTargets,
    allowCombine,
    backtrackr  // Pass backtrackr to hook
  });

  // Register keyboard shortcuts for undo/redo
  useEffect(() => {
    if (!useShortcut || !backtrackr) return;

    const handleUndo = async (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (backtrackr.canUndo) {
        await backtrackr.undo();
      }
    };

    const handleRedo = async (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (backtrackr.canRedo) {
        await backtrackr.redo();
      }
    };

    // Register with Commandr (intercepts browser defaults)
    const unregisterUndo = useShortcut('mod+z', handleUndo, {
      scope: 'visual-mapper',
      description: 'Undo mapping change',
      preventDefault: true
    });

    const unregisterRedo = useShortcut('mod+shift+z', handleRedo, {
      scope: 'visual-mapper',
      description: 'Redo mapping change',
      preventDefault: true
    });

    // Also support Ctrl+Y for redo (Windows convention)
    const unregisterRedoAlt = useShortcut('mod+y', handleRedo, {
      scope: 'visual-mapper',
      description: 'Redo mapping change (alt)',
      preventDefault: true
    });

    return () => {
      if (unregisterUndo) unregisterUndo();
      if (unregisterRedo) unregisterRedo();
      if (unregisterRedoAlt) unregisterRedoAlt();
    };
  }, [backtrackr, useShortcut]);

  // Handle field drag start
  const handleDragStart = useCallback((field: any, sourceType: 'source' | 'target') => {
    // Drag logic will be handled by FieldList component
  }, []);

  // Handle field drop
  const handleDrop = useCallback((sourceField: any, targetField: any) => {
    mapper.addMapping({
      sourceFields: [sourceField.id],
      targetField: targetField.id,
      enabled: true
    });
  }, [mapper]);

  // Get mapped status for fields
  const getSourceMappedStatus = useCallback((fieldId: string) => {
    return mapper.mappings.some(m => m.sourceFields.includes(fieldId) && m.enabled);
  }, [mapper.mappings]);

  const getTargetMappedStatus = useCallback((fieldId: string) => {
    return mapper.mappings.some(m => m.targetField === fieldId && m.enabled);
  }, [mapper.mappings]);

  // Get mapping for a field
  const getMappingsForField = useCallback((fieldId: string, type: 'source' | 'target') => {
    if (type === 'source') {
      return mapper.mappings.filter(m => m.sourceFields.includes(fieldId));
    } else {
      return mapper.mappings.filter(m => m.targetField === fieldId);
    }
  }, [mapper.mappings]);

  return (
    <div 
      className={`visual-mapper visual-mapper--${theme} visual-mapper--${layout} ${className}`}
      data-testid="visual-mapper"
    >
      {/* Toolbar */}
      {showToolbar && (
        <MapperToolbar
          onAutoMatch={mapper.autoMatch}
          onClearAll={mapper.clearMappings}
          onValidate={mapper.validate}
          isAutoMatching={mapper.isAutoMatching}
          validation={mapper.validation}
          mappingCount={mapper.mappings.length}
          theme={theme}
        />
      )}

      {/* Main mapping area */}
      <div className="visual-mapper__content">
        {/* Source fields */}
        <div className="visual-mapper__source">
          <EditableFieldList
            fields={localSourceFields}
            type="source"
            title="Source Fields"
            onFieldsChange={setLocalSourceFields}
            getMappedStatus={getSourceMappedStatus}
            showConfidence={showConfidence}
            theme={theme}
            editable={true}
          />
        </div>

        {/* Mapping canvas (connection lines) */}
        <div className="visual-mapper__canvas">
          <MappingCanvas
            mappings={mapper.mappings}
            sourceFields={sourceFields}
            targetFields={targetFields}
            selectedMappings={mapper.selectedMappings}
            onMappingClick={(mapping) => {
              mapper.selectMapping(mapping.id);
            }}
            onMappingRemove={(mappingId) => {
              mapper.removeMapping(mappingId);
            }}
            showConfidence={showConfidence}
            theme={theme}
          />
        </div>

        {/* Target fields */}
        <div className="visual-mapper__target">
          <EditableFieldList
            fields={localTargetFields}
            type="target"
            title="Target Fields"
            onFieldsChange={setLocalTargetFields}
            getMappedStatus={getTargetMappedStatus}
            showConfidence={showConfidence}
            theme={theme}
            editable={true}
          />
        </div>
      </div>

      {/* Suggestions panel */}
      {mapper.suggestions.length > 0 && (
        <SuggestionsPanel
          suggestions={mapper.suggestions}
          onApply={mapper.applySuggestion}
          onDismiss={(suggestion) => {
            // Remove suggestion
          }}
          theme={theme}
        />
      )}

      {/* Validation errors/warnings */}
      {mapper.validation && !mapper.validation.valid && (
        <div className="visual-mapper__validation">
          {mapper.validation.errors && mapper.validation.errors.length > 0 && (
            <div className="visual-mapper__errors">
              <h4>❌ Errors</h4>
              <ul>
                {mapper.validation.errors.map((error, idx) => (
                  <li key={idx}>
                    <strong>{error.code}:</strong> {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {mapper.validation.warnings && mapper.validation.warnings.length > 0 && (
            <div className="visual-mapper__warnings">
              <h4>⚠️ Warnings</h4>
              <ul>
                {mapper.validation.warnings.map((warning, idx) => (
                  <li key={idx}>
                    <strong>{warning.code}:</strong> {warning.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisualMapper;
